import React, { useState, useRef } from 'react';
import walletApi from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle,
  Users, Mail, SkipForward
} from 'lucide-react';
import { toast } from 'sonner';

export default function BulkEmployeeUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [sendInvites, setSendInvites] = useState(true);
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const resp = await walletApi.get('/company/employees/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_upload_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Download failed');
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('send_invites', sendInvites);
      const data = await walletApi.post('/company/employees/bulk-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => r.data);
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} employee(s) created successfully`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="bulk-upload-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Bulk Employee Upload</h1>
        <p className="text-sm text-gray-500">Upload a CSV file to create employee accounts and send login invitations</p>
      </div>

      {/* Step 1: Download Template */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Step 1: Download Template</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-gray-500 mb-3">
            Download the CSV template with the required columns. Fill it with your employee data.
          </p>
          <Button variant="outline" onClick={handleDownloadTemplate} className="border-gray-200" data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Download CSV Template
          </Button>
          <div className="mt-3 p-2.5 rounded-lg bg-gray-50 text-xs text-gray-500">
            <p className="font-medium text-gray-700 mb-1">Required columns:</p>
            <p>email, first_name, last_name</p>
            <p className="font-medium text-gray-700 mt-2 mb-1">Per-employee details:</p>
            <p>phone, department, job_title, gross_salary, employee_id, nin</p>
            <p className="font-medium text-gray-700 mt-2 mb-1">Employment terms (per employee):</p>
            <p>employment_type (full_time/part_time/contract), work_arrangement (onsite/remote/hybrid), probation_months, notice_period</p>
            <p className="font-medium text-gray-700 mt-2 mb-1">Other:</p>
            <p>gender, date_of_birth</p>
            <p className="mt-2 text-gray-400 italic">BVN is not required during upload — employees add their own BVN in their profile to activate their wallet.</p>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Upload File */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Step 2: Upload Employee Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${file ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/30'}`}
            data-testid="file-drop-zone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="w-8 h-8 text-emerald-500" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Click to select your CSV file</p>
                <p className="text-xs text-gray-400 mt-1">Max 1000 employees per upload</p>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileSelect} />

          <div className="flex items-center gap-2">
            <input type="checkbox" id="sendInvites" checked={sendInvites} onChange={e => setSendInvites(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-pink-500 focus:ring-pink-400" data-testid="send-invites-check" />
            <Label htmlFor="sendInvites" className="text-sm text-gray-700 cursor-pointer">
              Send email invitations to new employees
            </Label>
          </div>

          <Button onClick={handleUpload} disabled={!file || uploading} className="w-full bg-pink-500 hover:bg-pink-600 text-white" data-testid="upload-btn">
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload & Create Accounts</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="border-gray-200 shadow-sm" data-testid="upload-results">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Upload Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-emerald-50 text-center">
                <Users className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-emerald-700" data-testid="result-created">{result.created}</p>
                <p className="text-[10px] text-emerald-600">Created</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 text-center">
                <Mail className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-blue-700" data-testid="result-emails">{result.emails_sent}</p>
                <p className="text-[10px] text-blue-600">Emails Sent</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 text-center">
                <SkipForward className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-amber-700">{result.skipped}</p>
                <p className="text-[10px] text-amber-600">Skipped</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 text-center">
                <AlertCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-red-700">{result.errors}</p>
                <p className="text-[10px] text-red-600">Errors</p>
              </div>
            </div>

            {/* Created list */}
            {result.details?.created?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Created Employees:</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {result.details.created.map(emp => (
                    <div key={emp.email} className="flex items-center gap-2 p-2 rounded bg-emerald-50/50 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="font-medium text-gray-800">{emp.name}</span>
                      <span className="text-gray-500">{emp.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {result.details?.errors?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-700 mb-2">Errors:</p>
                {result.details.errors.map((err, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded bg-red-50/50 text-xs">
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    <span>Row {err.row}: {err.error}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Skipped */}
            {result.details?.skipped?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-amber-700 mb-2">Skipped (already exist):</p>
                {result.details.skipped.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded bg-amber-50/50 text-xs">
                    <SkipForward className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span>{s.email} - {s.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
